import { AsyncPipe, CommonModule, isPlatformBrowser } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	EventEmitter,
	inject,
	Input,
	input,
	model,
	OnChanges,
	OnInit,
	Output,
	output,
	PLATFORM_ID,
	signal,
	SimpleChanges,
} from '@angular/core';
import { Router, RouterLink } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';
import dayjs from 'dayjs';
import {
	BehaviorSubject,
	distinctUntilChanged,
	filter,
	first,
	map,
	Observable,
	shareReplay,
	switchMap,
	tap,
} from 'rxjs';

import {
	AvailabilityDatesInterface,
	AvailabilityRequestInterface,
} from '@valk-nx/availability-dates-store/availability-dates.interface';
import { AvailabilityDatesService } from '@valk-nx/availability-dates-store/availability-dates.service';
import { TabMenuComponent } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu';
import { TabMenuItemDirective } from '@valk-nx/components/ui-tab-menu/src/lib/tab-menu.directive';
import { cn, fallbackLanguage } from '@valk-nx/core/lib/core';
import {
	AccommodationTypeInterface,
	HotelInterface,
	OccupancyRestrictionsInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import { BookingRedirectHelper } from '@valk-nx/helpers/lib/booking-redirect/booking-redirect.helper';
import { OccupancyInterface } from '@valk-nx/helpers/lib/interfaces/occupancy.interface';
import { toDate } from '@valk-nx/helpers/lib/transformers/toDate';

import { AvailabilityWidgetInputDateRangeComponent } from './availability-input-date-range/availability-input-date-range';
import { AvailabilityWidgetInputDropdownComponent } from './availability-input-dropdown/availability-input-dropdown';
import { AvailabilityInputDropdownItem } from './availability-input-dropdown/availability-input-dropdown.interface';
import { AvailabilityWidgetInputOccupancyComponent } from './availability-input-occupancy/availability-input-occupancy';
import { validateDates } from './availability-date-validation.helper';
import {
	AvailabilityWidgetEmitInterface,
	WidgetDataActionInterface,
} from './availability-widget.component.interface';

export const occupancyRestrictions = {
	adults: {
		min: 1,
		max: 3,
	},
	children: {
		min: 0,
		max: 1,
	},
	infants: {
		min: 0,
		max: 1,
	},
	maxPersons: 2,
	maxOccupancies: 5,
};

const yesterday = dayjs().subtract(1, 'day').toDate();
type AvailabilityWidgetInput = 'hotels' | 'occupancy' | 'packages' | 'dates';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: `vp-availability-widget`,
	templateUrl: './availability-widget.component.html',
	imports: [
		AvailabilityWidgetInputDateRangeComponent,
		AvailabilityWidgetInputOccupancyComponent,
		AvailabilityWidgetInputDropdownComponent,
		AsyncPipe,
		CommonModule,
		TabMenuComponent,
		TabMenuItemDirective,
		TranslatePipe,
		RouterLink,
	],
})
export class AvailabilityWidgetComponent implements OnInit, OnChanges {
	private readonly availabilityService = inject(AvailabilityDatesService);
	private readonly mergedAvailability = signal<AvailabilityDatesInterface>(
		{},
	);
	readonly platformId = inject(PLATFORM_ID);
	private readonly router = inject(Router);
	private readonly updateCta = signal(0);

	@Input() hotelGuid: string | undefined;
	hotelSlug = input<string | undefined>(undefined);
	@Input() roomGuid: string | undefined;
	@Input() dealGuid: string | undefined;
	packageGuid = input<string | undefined>(undefined);
	promotionGuid = input<string | undefined>(undefined);

	accommodationTypes = input<AccommodationTypeInterface[] | undefined>();
	activeAccommodationTypeIndex = signal(0);
	selectedAccommodationType = model<string | undefined>(undefined);
	@Input() initialOccupancy: OccupancyInterface[] | undefined;
	@Input() defaultAdults = 2;
	occupancyRestrictions = input<OccupancyRestrictionsInterface>(
		occupancyRestrictions,
	);

	@Input({ transform: toDate }) initialArrivalDate: Date | undefined;
	@Input({ transform: toDate }) initialDepartureDate: Date | undefined;
	@Input() minDate = yesterday;
	@Input() maxDate = dayjs().add(1, 'year').toDate();

	@Input() hotels: AvailabilityInputDropdownItem[] = [];
	@Input() stepperConfig: {
		useStepper: boolean;
		min: number;
		max: number | undefined;
	} = { useStepper: false, min: 1, max: undefined };

	currency = input<string>('EUR');
	showHotels = input(false);
	showOccupancy = input(true);
	showDates = input(true);
	showPrices = input(true);
	showPackages = input(false);
	showAccommodationTypes = input(false);
	packages = input<AvailabilityInputDropdownItem[]>([]);
	forceHideRangePrices = input<boolean>(false);

	openAvailabilityInNewTab = input<boolean>(false);

	@Output() customEmitter: EventEmitter<AvailabilityWidgetEmitInterface> =
		new EventEmitter<AvailabilityWidgetEmitInterface>();
	actionClick = output<{
		event: string;
		eventType: string;
		data: WidgetDataActionInterface;
	}>();
	packageSelected = output<number>();
	occupancyChanged = output<OccupancyInterface[]>();
	hotelChanged = output<Partial<HotelInterface>>();
	selectedAccommodationTypeChange = output<string | undefined>();

	bookingtoolUrl = input<string>();
	@Input({ required: true }) language: string = fallbackLanguage;

	occupancy: OccupancyInterface[];

	updateAvailability$ = new BehaviorSubject<AvailabilityRequestInterface>(
		undefined,
	);
	isLoading = false;
	jumpToFirstAvailableDate = true;

	availability$: Observable<AvailabilityDatesInterface>;
	currentAvailability = signal<AvailabilityDatesInterface | null>(null);
	firstDateInCalendar: string = dayjs().format('YYYY-MM-DD');
	openInput = signal<AvailabilityWidgetInput | null>(null);
	isOccupancyOpen = computed(() => this.openInput() === 'occupancy');
	isHotelsOpen = computed(() => this.openInput() === 'hotels');
	isPackagesOpen = computed(() => this.openInput() === 'packages');
	isDatePickerOpen = computed(() => this.openInput() === 'dates');
	/**
	 * Determines the applicable occupancy restrictions based on the selected accommodation type.
	 * If a specific accommodation type is selected and it defines its own restrictions,
	 * those are used. Otherwise, it falls back to the default occupancy restrictions.
	 * The fallback for undefined occupancyRestrictions is for if the component uses the old data structure
	 */
	activeOccupancyRestrictions = computed(() => {
		const selectedGuid = this.selectedAccommodationType();
		const types = this.accommodationTypes();

		if (selectedGuid && types) {
			const selectedType = types.find(
				(type) => type.guid === selectedGuid,
			);
			if (selectedType.occupancyRestrictions) {
				return selectedType.occupancyRestrictions;
			}
		}

		return this.occupancyRestrictions();
	});
	selectedHotel: Partial<HotelInterface>;
	selectedPackage = model<AvailabilityInputDropdownItem>();
	nights = 1;

	arrivalDate: Date;
	departureDate: Date;
	_resetDateRange = 0;
	_dateUpdateSource: 'stepper' | 'programmatic' | 'manual' | null = null;
	lastRequestKey: string | null = null;
	lastRequestDateRange: {
		startDate: string;
		endDate: string;
	} | null = null;

	cn = cn;

	constructor() {
		effect(() => {
			const availability = this.currentAvailability();
			if (availability) {
				// Validate initial dates (from URL/Input)
				if (this.initialArrivalDate && this.initialDepartureDate) {
					this.validateDatesAgainstAvailability();
					// Only reset if it was a programmatic update (from URL/Input)
					if (this._dateUpdateSource === 'programmatic') {
						this._dateUpdateSource = null;
					}
				}

				// Validate user-selected dates
				// Check both arrival and departure dates - clear if unavailable
				if (this.arrivalDate) {
					this.validateSelectedArrivalDate(availability);
				}

				// Reset _dateUpdateSource after validation so that subsequent
				// availability updates can validate dates (e.g., when dates become unavailable)
				// This allows validation to run even after month navigation
				this._dateUpdateSource = null;
			}
		});

		this.availability$ = this.updateAvailability$.pipe(
			filter(
				(request) =>
					!!request && !!request.hotelGUID && !!request.occupancies,
			),
			distinctUntilChanged(areAvailabilityRequestsEqual),
			tap(() => (this.isLoading = true)),
			switchMap((request) => {
				// Create a key to detect when key parameters change (hotel, occupancy, etc.)
				const requestKey = this.getRequestKey(request);
				const shouldReset = this.lastRequestKey !== requestKey;

				if (shouldReset) {
					this.lastRequestKey = requestKey;
					this.mergedAvailability.set({});
				}

				// Track the date range for this request
				this.lastRequestDateRange = {
					startDate: request.startDate,
					endDate: request.endDate,
				};
				return this.availabilityService
					.getAvailabilityDates(
						request.hotelGUID,
						request.endDate,
						request.startDate,
						request.occupancies,
						request.accommodationTypes,
						request.dealGUID,
						request.packageGUID,
						request.roomGUID,
						request.nights,
						request.promotionGUID,
					)
					.pipe(
						map((newAvailability) =>
							this.mergeAvailability(
								newAvailability,
								request.startDate,
								request.endDate,
								shouldReset,
							),
						),
					);
			}),
			tap((availability) => {
				this.isLoading = false;
				this.currentAvailability.set(availability);
			}),
			shareReplay(1),
		);

		effect(() => {
			const currentPackage = this.packages()?.find(
				// TODO: Fix typing in VPL-31625
				(item) => (item.guid ?? item['GUID']) === this.packageGuid(),
			);
			/* istanbul ignore else */
			if (currentPackage) {
				this.onPackageSelect(currentPackage);
			}
		});

		// if a selectedAccommodatinTypeGuid changes, the input has been changed
		effect(() => {
			const selectedAccommodationTypeGuid =
				this.selectedAccommodationType();
			if (!selectedAccommodationTypeGuid) {
				this.activeAccommodationTypeIndex.set(0);
				return;
			}

			const selectedindex = this.accommodationTypes().findIndex(
				(accommodationType) =>
					accommodationType.guid === selectedAccommodationTypeGuid,
			);

			// we have to compensate 'All' which is at index 0
			this.activeAccommodationTypeIndex.set(selectedindex + 1);
		});

		// if the activeAccommodationType changes, an accommodationType tab as been changed
		effect(() => {
			const index = this.activeAccommodationTypeIndex();
			if (index === 0 || index > this.accommodationTypes().length) {
				// 'All'
				this.selectedAccommodationType.set(undefined);
			} else {
				this.selectedAccommodationType.set(
					this.accommodationTypes()[index - 1]?.guid,
				);
			}
			this.selectedAccommodationTypeChange.emit(
				this.selectedAccommodationType(),
			);
			this.getAvailability();
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		// Track when dates are set programmatically (from @Input, e.g., URL params)
		if (changes['initialArrivalDate'] || changes['initialDepartureDate']) {
			this._dateUpdateSource = 'programmatic';
		}

		if (changes['initialOccupancy'] && this.showOccupancy()) {
			const newInitialOccupancy =
				changes['initialOccupancy'].currentValue;
			// Only update if the value actually changed (prevent loops from parent recalculations)
			if (
				newInitialOccupancy &&
				JSON.stringify(newInitialOccupancy) !==
					JSON.stringify(this.occupancy)
			) {
				this.occupancy = newInitialOccupancy;
			}
		}

		if (changes['occupancyRestrictions']) {
			this.getAvailability();
		}

		if (changes['promotionGuid'] && !changes['promotionGuid'].firstChange) {
			this.getAvailability();
		}

		this.updateCta.update((v) => v + 1);
	}

	ngOnInit() {
		this.nights = this.stepperConfig?.min ?? 1;

		if (this.minDate && this.minDate.toString() !== yesterday.toString()) {
			this.firstDateInCalendar = dayjs(this.minDate).format('YYYY-MM-DD');
		}

		if (
			this.initialArrivalDate &&
			this.minDate.toString() === yesterday.toString()
		) {
			this.firstDateInCalendar = dayjs(this.initialArrivalDate)
				.startOf('month')
				.format('YYYY-MM-DD');
		}

		if (!this.showOccupancy()) {
			this.occupancy = [
				{ adults: this.defaultAdults, children: 0, infants: 0 },
			];
		} else {
			this.occupancy = this.initialOccupancy;
		}

		// Sync tab index from initial parent value (only once, not in an effect)
		const initialGuid = this.selectedAccommodationType();
		if (initialGuid && this.accommodationTypes()) {
			const selectedindex = this.accommodationTypes().findIndex(
				(accommodationType) => accommodationType.guid === initialGuid,
			);
			if (selectedindex >= 0) {
				this.activeAccommodationTypeIndex.set(selectedindex + 1);
			}
		}

		// Validate dates against stepperConfig constraints (resolver ensures deal is loaded)
		this.validateDatesAgainstStepperConfig();
		this.setNights();
		this.getAvailability();

		// if there is no initialArrivalDate or initialDepartureDate
		// find the first available and fill them in
		if (!this.initialArrivalDate || !this.initialDepartureDate) {
			this.availability$
				.pipe(first())
				.subscribe((availability) =>
					this.setInitialDates(availability),
				);
		}
	}

	// sets the intialArrivalDate and initialDepartureDate if they are not set yet, based on the given availability dates response
	private setInitialDates(availability: AvailabilityDatesInterface) {
		if (!this.initialArrivalDate && Object.keys(availability).length > 0) {
			this.initialArrivalDate = toDate(
				Object.keys(availability)[0],
			) as Date;
		}
		if (!this.initialDepartureDate && this.initialArrivalDate) {
			const arrivalKey = dayjs(this.initialArrivalDate).format(
				'YYYY-MM-DD',
			);
			const departureDates = availability[arrivalKey];
			if (departureDates && Object.keys(departureDates).length > 0) {
				this.initialDepartureDate = toDate(
					Object.keys(departureDates)[0],
				) as Date;
			}
		}
	}

	private validateDatesAgainstStepperConfig() {
		if (!this.hasDates() || !this.stepperConfig?.useStepper) {
			return;
		}

		const calculatedNights = this.calculateNights();
		const { min = 1, max } = this.stepperConfig;

		const isValid =
			calculatedNights >= min && (!max || calculatedNights <= max);

		if (!isValid) {
			this.clearDatesAndResetNights(min);
		}
	}

	private setNights() {
		if (this.stepperConfig?.useStepper && this.hasDates()) {
			this.nights = this.calculateNights();
		}
	}

	private validateDatesAgainstAvailability() {
		if (!this.hasDates()) {
			return;
		}

		// Skip validation for stepper and manual updates
		// But validate programmatic updates (from URL/Input) to clear invalid dates
		if (
			this._dateUpdateSource === 'stepper' ||
			this._dateUpdateSource === 'manual'
		) {
			return;
		}

		const availability = this.currentAvailability();
		if (!availability) {
			return;
		}

		const arrivalKey = dayjs(this.initialArrivalDate).format('YYYY-MM-DD');
		const departureKey = dayjs(this.initialDepartureDate).format(
			'YYYY-MM-DD',
		);

		const isAvailable = !!availability[arrivalKey]?.[departureKey];

		if (!isAvailable) {
			this.clearDatesAndResetNights();
		}
	}

	private validateSelectedArrivalDate(
		availability: AvailabilityDatesInterface,
	): void {
		if (!this.arrivalDate) {
			return;
		}

		const arrivalKey = dayjs(this.arrivalDate).format('YYYY-MM-DD');
		const departureKey = this.departureDate
			? dayjs(this.departureDate).format('YYYY-MM-DD')
			: null;

		// Skip validation ONLY for stepper updates
		// For all other cases (including manual/month navigation), validate dates
		// that are in the request range to ensure they're still available
		if (this._dateUpdateSource === 'stepper') {
			return;
		}

		// Validate both arrival and departure dates using shared helper
		const { isArrivalValid, isDepartureValid } = validateDates(
			availability,
			arrivalKey,
			departureKey || undefined,
		);

		if (!isArrivalValid || !isDepartureValid) {
			this.arrivalDate = null;
			this.departureDate = null;
			this._resetDateRange++;
			this.updateCta.update((v) => v + 1);
		}
	}

	private getRequestKey(request: AvailabilityRequestInterface): string {
		// Create a unique key based on key parameters (hotel, occupancy, etc.)
		// Date range is not included as we want to merge across date ranges
		return [
			request.hotelGUID,
			JSON.stringify(request.occupancies),
			JSON.stringify(request.accommodationTypes || []),
			request.dealGUID,
			request.packageGUID,
			request.roomGUID,
			request.promotionGUID,
		].join('|');
	}

	private mergeAvailability(
		newAvailability: AvailabilityDatesInterface,
		requestStartDate: string,
		requestEndDate: string,
		shouldReset: boolean,
	): AvailabilityDatesInterface {
		if (shouldReset) {
			this.mergedAvailability.set({ ...newAvailability });
			return this.mergedAvailability();
		}

		const merged = { ...this.mergedAvailability() };
		this.removeOldAvailabilityData(
			merged,
			requestStartDate,
			requestEndDate,
		);
		this.addNewAvailabilityData(
			merged,
			newAvailability,
			requestStartDate,
			requestEndDate,
		);

		this.mergedAvailability.set(merged);
		return merged;
	}

	private isDateInRange(
		date: string,
		startDate: string,
		endDate: string,
	): boolean {
		return date >= startDate && date <= endDate;
	}

	private removeOldAvailabilityData(
		merged: AvailabilityDatesInterface,
		requestStartDate: string,
		requestEndDate: string,
	): void {
		for (const availableArrivalDate in merged) {
			if (
				this.isDateInRange(
					availableArrivalDate,
					requestStartDate,
					requestEndDate,
				)
			) {
				delete merged[availableArrivalDate];
			} else {
				this.removeDepartureDatesInRange(
					merged,
					availableArrivalDate,
					requestStartDate,
					requestEndDate,
				);
			}
		}
	}

	private removeDepartureDatesInRange(
		merged: AvailabilityDatesInterface,
		arrivalDate: string,
		requestStartDate: string,
		requestEndDate: string,
	): void {
		const mergedDepartures = merged[arrivalDate];
		// Don't remove departure dates from arrival dates outside the request range
		// This preserves departure dates (e.g., Feb dates for Jan arrivals) when navigating to new months
		// Only remove if the arrival date itself is being replaced by new data
		if (this.isDateInRange(arrivalDate, requestStartDate, requestEndDate)) {
			// If arrival date is in range, it will be replaced entirely, so remove departures
			for (const availableDepartureDate in mergedDepartures) {
				if (
					this.isDateInRange(
						availableDepartureDate,
						requestStartDate,
						requestEndDate,
					)
				) {
					delete mergedDepartures[availableDepartureDate];
				}
			}
		}
		// If arrival date is outside range, keep all its departure dates
		if (Object.keys(mergedDepartures).length === 0) {
			delete merged[arrivalDate];
		}
	}

	private addNewAvailabilityData(
		merged: AvailabilityDatesInterface,
		newAvailability: AvailabilityDatesInterface,
		requestStartDate: string,
		requestEndDate: string,
	): void {
		for (const arrivalDate in newAvailability) {
			if (
				newAvailability[arrivalDate] &&
				this.isDateInRange(
					arrivalDate,
					requestStartDate,
					requestEndDate,
				)
			) {
				merged[arrivalDate] = {
					...newAvailability[arrivalDate],
				};
			}
		}
	}

	private hasDates(): boolean {
		return !!(this.initialArrivalDate && this.initialDepartureDate);
	}

	private calculateNights(): number {
		return dayjs(this.initialDepartureDate).diff(
			dayjs(this.initialArrivalDate),
			'days',
		);
	}

	private clearDatesAndResetNights(
		minNights = this.stepperConfig?.min ?? 1,
	): void {
		this.initialArrivalDate = null;
		this.initialDepartureDate = null;
		this.arrivalDate = null;
		this.departureDate = null;
		this._resetDateRange++;
		this.nights = minNights;
		this.updateCta.update((v) => v + 1);
	}

	// Token to inform child tab menu to refresh when language or store-provided
	// accommodation type names change.
	get tabMenuRefreshToken(): string {
		const names =
			this.accommodationTypes()
				?.map((a) => a.name)
				.join('|') ?? '';
		return `${this.language}|${names}`;
	}

	onCtaEmit(event: Event, showPackages = false) {
		this.openInput.set(showPackages ? 'packages' : 'dates');

		this.getAvailability();

		event.stopPropagation();
	}

	onBackButton(
		event: Event,
		isHotelsOpen: boolean,
		isOccupancyOpen: boolean,
		isPackagesOpen: boolean,
		isDatePickerOpen: boolean,
	) {
		let inputToOpen: AvailabilityWidgetInput | null = null;

		if (isDatePickerOpen) inputToOpen = 'dates';
		else if (isPackagesOpen) inputToOpen = 'packages';
		else if (isOccupancyOpen) inputToOpen = 'occupancy';
		else if (isHotelsOpen) inputToOpen = 'hotels';

		this.openInput.set(inputToOpen);

		event.stopPropagation();
	}

	onDatePickerOpened(isOpen: boolean) {
		this.onInputOpenChange('dates', isOpen);
	}

	onOccupancyOpenEmit(isOpen: boolean) {
		this.onInputOpenChange('occupancy', isOpen);
	}

	onOccupancyEmit(occupancy: OccupancyInterface[]) {
		this.occupancy = occupancy;
		this.updateCta.update((v) => v + 1);
		this.occupancyChanged.emit(occupancy);
		this.getAvailability();
	}

	onHotelSelect(hotel: Partial<HotelInterface>) {
		this.selectedHotel = hotel;
		this.updateCta.update((v) => v + 1);
		this.hotelChanged.emit(hotel);
		this.getAvailability();
	}

	onPackageSelect(packageItem: AvailabilityInputDropdownItem) {
		// TODO: Fix typing in VPL-31625
		const currentPackageIndex = this.packages().findIndex(
			(item) =>
				(item.guid ?? item['GUID']) ===
				(packageItem.guid ?? packageItem.GUID),
		);

		this.selectedPackage.set(packageItem);
		this.packageSelected.emit(currentPackageIndex);
		this.resetDateRange();
		this.getAvailability();
	}

	resetDateRange() {
		this._resetDateRange++;
	}

	onInputOpenChange(input: AvailabilityWidgetInput, isOpen: boolean): void {
		const currentState = this.openInput();
		const newState = isOpen ? input : null;

		// Only update state if it's actually changing to prevent closing popover
		// when availability changes trigger change detection during month navigation
		if (currentState === newState) {
			return;
		}

		// Only close other inputs if we're opening a different input
		// This prevents flickering when the same input is already open
		if (isOpen && currentState !== input) {
			this.closeAllInputs();
		}

		this.setInputState(input, isOpen);
	}

	private closeAllInputs(): void {
		this.openInput.set(null);
	}

	private setInputState(
		input: AvailabilityWidgetInput,
		isOpen: boolean,
	): void {
		this.openInput.set(isOpen ? input : null);
	}

	recalculateAvailabilityForToday() {
		this.firstDateInCalendar = dayjs()
			.startOf('month')
			.format('YYYY-MM-DD');
		this.getAvailability();
	}

	onMonthChange(newFirstDate: string) {
		this.firstDateInCalendar = newFirstDate;
		this.jumpToFirstAvailableDate = false;
		this._dateUpdateSource = 'manual';
		this.getAvailability();
	}

	onStepperUpdated(nights: number) {
		this._dateUpdateSource = 'stepper';
		this.nights = nights;
		this.jumpToFirstAvailableDate = false;
		this.getAvailability();
	}

	getAvailability() {
		/* istanbul ignore else */
		if (isPlatformBrowser(this.platformId)) {
			const accommodationTypes =
				this.activeAccommodationTypeIndex() === 0
					? []
					: [
							this.accommodationTypes()[
								this.activeAccommodationTypeIndex() - 1
							]?.guid,
						];

			// Calculate date range for visible months (2 months from firstDateInCalendar)
			const startDate = this.firstDateInCalendar;
			const endDate = dayjs(this.firstDateInCalendar)
				.add(2, 'month')
				.format('YYYY-MM-DD');

			const request: AvailabilityRequestInterface = {
				accommodationTypes,
				hotelGUID: this.hotelGuid
					? this.hotelGuid
					: this.selectedHotel?.guid,
				startDate,
				endDate,
				occupancies: this.occupancy,
				dealGUID: this.dealGuid,
				packageGUID: this.showPackages()
					? (this.selectedPackage()?.['GUID'] ?? this.packageGuid())
					: undefined,
				roomGUID: this.roomGuid,
				promotionGUID: this.promotionGuid(),
			};

			if (this.stepperConfig?.useStepper) {
				request.nights = this.nights;
			}
			this.updateAvailability$.next(request);
		}
	}

	updateModel(event) {
		this.arrivalDate = event.arrivalDate;
		this.departureDate = event.departureDate;
		this.updateCta.update((v) => v + 1);
		// Don't mark as manual here - we want to validate selected dates
		// The validation will only clear if dates are truly unavailable
		this.getAvailability();
	}

	bookingtoolQuery = computed(() => {
		this.updateCta();

		const accommodationType =
			this.activeAccommodationTypeIndex() === 0
				? undefined
				: this.accommodationTypes()[
						this.activeAccommodationTypeIndex() - 1
					]?.accommodationType;

		const effectiveArrivalDate =
			this.arrivalDate || this.initialArrivalDate;
		const effectiveDepartureDate =
			this.departureDate || this.initialDepartureDate;

		return {
			roomGuid: this.roomGuid,
			dealGuid: this.dealGuid,
			packageGuid:
				(this.selectedPackage()?.['GUID'] ||
					this.selectedPackage()?.guid) ??
				this.packageGuid(),
			languageCode: this.language,
			accommodationTypes: accommodationType,
			startDate: effectiveArrivalDate
				? dayjs(effectiveArrivalDate).format('YYYY-MM-DD')
				: null,
			endDate: effectiveDepartureDate
				? dayjs(effectiveDepartureDate).format('YYYY-MM-DD')
				: null,
			occupancy: JSON.stringify(this.occupancy),
		};
	});

	isExternalUrl = computed(() => !!this.bookingtoolUrl());

	bookingtoolHref = computed(() => {
		this.updateCta();

		if (
			!this.bookingtoolUrl() ||
			!this.arrivalDate ||
			!this.departureDate
		) {
			return null;
		}

		const accommodationType =
			this.activeAccommodationTypeIndex() === 0
				? undefined
				: this.accommodationTypes()[
						this.activeAccommodationTypeIndex() - 1
					]?.accommodationType;

		const bookingUrl = BookingRedirectHelper.getRedirectUrl(
			this.bookingtoolUrl(),
			this.selectedHotel ? this.selectedHotel.slug : this.hotelSlug(),
			{
				roomGuid: this.roomGuid,
				dealGuid: this.dealGuid,
				packageGuid:
					(this.selectedPackage()?.['GUID'] ||
						this.selectedPackage()?.guid) ??
					this.packageGuid(),
				languageCode: this.language,
				accommodationTypes: accommodationType,
			},
		);

		const url = new URL(bookingUrl);
		url.searchParams.set(
			'startDate',
			dayjs(this.arrivalDate).format('YYYY-MM-DD'),
		);
		url.searchParams.set(
			'endDate',
			dayjs(this.departureDate).format('YYYY-MM-DD'),
		);
		if (this.occupancy?.length) {
			url.searchParams.set('occupancy', JSON.stringify(this.occupancy));
		}

		return url.toString();
	});

	isCtaDisabled = computed(() => {
		this.updateCta();

		return (
			(this.showHotels() && !this.selectedHotel) ||
			!(this.bookingtoolHref() || this.bookingtoolQuery())
		);
	});

	onCtaClick(event: Event) {
		if (!this.arrivalDate || !this.departureDate) {
			event.preventDefault();

			if (this.showHotels() && !this.selectedHotel) return;

			this.onDatePickerOpened(true);
			return;
		}

		if (this.isCtaDisabled()) {
			event.preventDefault();
			return;
		}

		this.closeAllInputs();
		this.setEmittedData();
	}

	setEmittedData() {
		const eventType = () => {
			if (this.packageGuid()) {
				return 'packages';
			} else if (this.roomGuid) {
				return 'rooms';
			} else if (this.dealGuid) {
				return 'deals';
			} else {
				return 'default';
			}
		};
		const rooms = this.occupancy.map((occupancy, i) => ({
			item_name: `ROOM${i + 1}`,
			numAdults: occupancy.adults,
			numChildren: occupancy.children,
			numBabies: occupancy.infants,
		}));
		const dayTypeBooking =
			dayjs().day() === 0 || dayjs().day() === 6 ? 'weekend' : 'weekday';
		const dayTypeVisit =
			dayjs(this.arrivalDate).day() === 5 ||
			dayjs(this.arrivalDate).day() === 6
				? 'weekend'
				: 'weekday';

		this.actionClick.emit({
			event: 'search_availability',
			eventType: eventType(),
			data: {
				dayTypeBooking,
				dayTypeVisit,
				dealGuid: this.dealGuid,
				hotelGuid: this.hotelGuid,
				leadtime: dayjs(this.arrivalDate).diff(
					dayjs().format('YYYY-MM-DD'),
					'day',
				),
				numGuests: this.occupancy.reduce((acc, curr) => {
					return acc + curr.adults + curr.children + curr.infants;
				}, 0),
				numRooms: this.occupancy.length,
				numNights: dayjs(this.departureDate).diff(
					dayjs(this.arrivalDate),
					'days',
				),
				packageGuid:
					(this.selectedPackage()?.['GUID'] ||
						this.selectedPackage()?.guid) ??
					this.packageGuid(),
				roomGuid: this.roomGuid,
				rooms,
				selectedArrivalDate: dayjs(this.arrivalDate).format(
					'YYYY-MM-DD',
				),
				selectedDepartureDate: dayjs(this.departureDate).format(
					'YYYY-MM-DD',
				),
			},
		});
	}
}

/**
 * Compares two AvailabilityRequestInterface objects for equality,
 * used primarily for the distinctUntilChanged operator.
 * It performs a shallow comparison of most properties and a deep comparison
 * for `occupancies` and `accommodationTypes` arrays using JSON.stringify.
 * @param prev The previous AvailabilityRequestInterface object.
 * @param curr The current AvailabilityRequestInterface object.
 * @returns True if the requests are considered equal, false otherwise.
 */
export function areAvailabilityRequestsEqual(
	prev: AvailabilityRequestInterface,
	curr: AvailabilityRequestInterface,
): boolean {
	if (!prev || !curr) return false;
	const shallowEqual =
		prev.hotelGUID === curr.hotelGUID &&
		prev.startDate === curr.startDate &&
		prev.endDate === curr.endDate &&
		prev.dealGUID === curr.dealGUID &&
		prev.packageGUID === curr.packageGUID &&
		prev.roomGUID === curr.roomGUID &&
		prev.nights === curr.nights &&
		prev.promotionGUID === curr.promotionGUID;
	const occupanciesEqual =
		JSON.stringify(prev.occupancies) === JSON.stringify(curr.occupancies);
	const accommodationTypesEqual =
		JSON.stringify(prev.accommodationTypes) ===
		JSON.stringify(curr.accommodationTypes);
	return shallowEqual && occupanciesEqual && accommodationTypesEqual;
}
